import { useEffect, useState } from "react";
import { Sparkles, Lock, ArrowRight, Info } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { isRunningInTwa, hasPlayBilling, getRuntimeDiagnostics, PLAY_SKUS } from "@/lib/twa";

// Soft paywall shown after trial ends. On Android TWA, triggers Play Billing
// via the Digital Goods API. On the web, gently directs the user to install
// the Android app — per Google Play policy we do not take web payments.
const COPY = {
  en: {
    badge: "Free trial ended",
    title: "Keep walking, deeply.",
    sub: "Your 7-day reflection window has closed. Continue your Vedic journey with a Vedari subscription — cancel anytime from Play Store.",
    monthly: "Monthly",
    yearly: "Yearly",
    perMonth: "/month",
    perYear: "/year",
    bestValue: "Best value",
    save: "Save ~17%",
    cta: "Subscribe",
    webHeading: "Subscriptions are on Android",
    webBody: "Per Google Play policy, subscriptions are only available through our Android app. Install Vedari from Play Store to continue your journey.",
    webCta: "Get the Android app",
    twaNoBillingHeading: "App update needed",
    twaNoBillingBody: "Your installed Vedari version doesn't have Play Billing enabled yet. Please update from Play Store to subscribe.",
    twaNoBillingCta: "Update on Play Store",
    todayLink: "Open Today",
    legal: "Cancel anytime · 7-day trial offered once per account · Managed by Google Play",
    purchasing: "Opening Google Play…",
    verifying: "Verifying with Google Play…",
    purchaseFailed: "Payment was not completed.",
    verifyFailed: "We couldn't confirm your subscription. Please try again or contact support.",
    notAvailable: "Subscriptions aren't available here. Please open Vedari on Android.",
    success: "Subscription active. Welcome back, traveler.",
  },
  hi: {
    badge: "नि:शुल्क परीक्षण समाप्त",
    title: "गहराई से चलते रहें।",
    sub: "आपकी 7-दिन की चिंतन-खिड़की पूरी हो गई है। Vedari सदस्यता के साथ अपनी वैदिक यात्रा जारी रखें — Play Store से कभी भी रद्द करें।",
    monthly: "मासिक",
    yearly: "वार्षिक",
    perMonth: "/माह",
    perYear: "/वर्ष",
    bestValue: "सबसे अच्छा मूल्य",
    save: "~17% बचत",
    cta: "सदस्यता लें",
    webHeading: "सदस्यता Android पर उपलब्ध है",
    webBody: "Google Play की नीति के अनुसार, सदस्यता केवल हमारे Android ऐप के माध्यम से उपलब्ध है। यात्रा जारी रखने के लिए Play Store से Vedari स्थापित करें।",
    webCta: "Android ऐप पाएँ",
    twaNoBillingHeading: "ऐप अपडेट आवश्यक",
    twaNoBillingBody: "आपके Vedari संस्करण में अभी Play Billing सक्षम नहीं है। सदस्यता लेने के लिए कृपया Play Store से अपडेट करें।",
    twaNoBillingCta: "Play Store पर अपडेट करें",
    todayLink: "आज खोलें",
    legal: "कभी भी रद्द करें · प्रति खाता एक 7-दिन का परीक्षण · Google Play द्वारा संचालित",
    purchasing: "Google Play खुल रहा है…",
    verifying: "Google Play से सत्यापित किया जा रहा है…",
    purchaseFailed: "भुगतान पूरा नहीं हुआ।",
    verifyFailed: "हम आपकी सदस्यता की पुष्टि नहीं कर सके। कृपया पुनः प्रयास करें।",
    notAvailable: "यहाँ सदस्यता उपलब्ध नहीं है। कृपया Android पर Vedari खोलें।",
    success: "सदस्यता सक्रिय। पुनः स्वागत है, यात्री।",
  },
};

// Pre-fetch SKU details once on mount so the click handler can call
// `PaymentRequest.show()` synchronously and avoid losing user activation.
async function fetchSkuDetails() {
  const service = await window.getDigitalGoodsService("https://play.google.com/billing");
  if (!service) throw new Error("DGAPI: getDigitalGoodsService returned null");
  const details = await service.getDetails([PLAY_SKUS.monthly, PLAY_SKUS.yearly]);
  return { service, details };
}

// Buy synchronously (must be called from inside a user-activation handler).
async function buyOnPlay(sku, details) {
  const item = details.find((d) => d.itemId === sku);
  if (!item) throw new Error(`SKU '${sku}' not configured in Play Console`);
  const paymentMethodData = [{
    supportedMethods: "https://play.google.com/billing",
    data: { sku },
  }];
  const paymentDetails = {
    total: {
      label: item.title || sku,
      amount: {
        currency: item.price?.currency || "INR",
        // Play uses its own pricing — we just pass a valid placeholder.
        value: item.price?.value || "0",
      },
    },
  };
  const request = new PaymentRequest(paymentMethodData, paymentDetails);
  const response = await request.show();
  const purchaseToken = response?.details?.token;
  await response.complete("success");
  return { sku, purchaseToken };
}

export default function Paywall({ onSubscribed }) {
  const { lang } = useLang();
  const C = COPY[lang] || COPY.en;
  const inTwa = isRunningInTwa();
  const billingAvailable = hasPlayBilling();
  const [busy, setBusy] = useState(null); // 'monthly' | 'yearly' | null
  const [showDiag, setShowDiag] = useState(false);
  const [skuDetails, setSkuDetails] = useState(null);
  const [skuError, setSkuError] = useState(null);
  const [lastError, setLastError] = useState(null);
  const diag = getRuntimeDiagnostics();

  // Pre-fetch SKU details on mount so the buy click handler doesn't
  // burn the user-activation window on network awaits.
  useEffect(() => {
    if (!billingAvailable) return;
    let cancelled = false;
    (async () => {
      try {
        const { details } = await fetchSkuDetails();
        if (!cancelled) setSkuDetails(details);
      } catch (e) {
        if (!cancelled) setSkuError(`${e?.name || "Error"}: ${e?.message || String(e)}`);
      }
    })();
    return () => { cancelled = true; };
  }, [billingAvailable]);

  const handleBuy = async (plan) => {
    setLastError(null);
    if (!billingAvailable) {
      toast.message(C.notAvailable);
      return;
    }
    if (!skuDetails) {
      const msg = skuError || "Loading plans…";
      toast.error(msg);
      setLastError(msg);
      return;
    }
    const sku = PLAY_SKUS[plan];
    setBusy(plan);
    try {
      const { purchaseToken } = await buyOnPlay(sku, skuDetails);
      toast.message(C.verifying);
      await api.post("/billing/google-play/verify", { sku, purchaseToken });
      toast.success(C.success);
      onSubscribed?.();
    } catch (e) {
      const code = e?.response?.status;
      const detail = `${e?.name || "Error"}: ${e?.message || String(e)}`;
      setLastError(detail);
      if (code === 402) toast.error(C.verifyFailed);
      else if (code === 503) toast.message(C.notAvailable);
      else toast.error(detail);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div
      data-testid="paywall"
      className="min-h-[calc(100vh-64px)] flex flex-col px-6 pt-10 pb-32"
      style={{ backgroundColor: "var(--vedari-cream)", color: "var(--vedari-ink)" }}
    >
      <div className="flex items-center gap-2 mb-6">
        <Lock size={14} strokeWidth={1.75} style={{ color: "var(--vedari-gold)" }} />
        <span
          data-testid="paywall-badge"
          className="text-xs uppercase tracking-[0.2em] font-medium"
          style={{ color: "var(--vedari-gold)" }}
        >
          {C.badge}
        </span>
      </div>

      <h1
        className="text-display text-4xl sm:text-5xl leading-tight mb-4"
        style={{ fontFamily: "'Cormorant Garamond', serif", color: "var(--vedari-ink)" }}
      >
        {C.title}
      </h1>
      <p className="text-body leading-relaxed mb-10" style={{ color: "var(--vedari-ink-soft)" }}>
        {C.sub}
      </p>

      {billingAvailable ? (
        <div className="space-y-4">
          <button
            data-testid="paywall-buy-monthly"
            disabled={busy !== null}
            onClick={() => handleBuy("monthly")}
            className="w-full rounded-2xl border p-5 flex items-center justify-between transition-all active:scale-[0.99] disabled:opacity-60"
            style={{ borderColor: "var(--vedari-divider)", backgroundColor: "var(--vedari-surface)" }}
          >
            <div className="text-left">
              <div className="text-sm uppercase tracking-wider" style={{ color: "var(--vedari-ink-soft)" }}>
                {C.monthly}
              </div>
              <div className="key-data text-2xl mt-1">₹190<span className="text-sm font-normal opacity-70">{C.perMonth}</span></div>
            </div>
            <ArrowRight size={18} strokeWidth={1.75} style={{ color: "var(--vedari-gold)" }} />
          </button>

          <button
            data-testid="paywall-buy-yearly"
            disabled={busy !== null}
            onClick={() => handleBuy("yearly")}
            className="w-full rounded-2xl border-2 p-5 flex items-center justify-between transition-all active:scale-[0.99] disabled:opacity-60 relative"
            style={{ borderColor: "var(--vedari-gold)", backgroundColor: "var(--vedari-surface)" }}
          >
            <span
              className="absolute -top-2 left-5 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider"
              style={{ backgroundColor: "var(--vedari-gold)", color: "var(--vedari-cream)" }}
            >
              <Sparkles size={10} className="inline mr-1" strokeWidth={2} />
              {C.bestValue}
            </span>
            <div className="text-left">
              <div className="text-sm uppercase tracking-wider" style={{ color: "var(--vedari-ink-soft)" }}>
                {C.yearly} · {C.save}
              </div>
              <div className="key-data text-2xl mt-1">₹1899<span className="text-sm font-normal opacity-70">{C.perYear}</span></div>
            </div>
            <ArrowRight size={18} strokeWidth={1.75} style={{ color: "var(--vedari-gold)" }} />
          </button>

          <p className="text-xs text-center mt-6 leading-relaxed" style={{ color: "var(--vedari-ink-soft)" }}>
            {C.legal}
          </p>
        </div>
      ) : (
        <div
          data-testid={inTwa ? "paywall-twa-no-billing" : "paywall-web-card"}
          className="rounded-2xl border p-6"
          style={{ borderColor: "var(--vedari-divider)", backgroundColor: "var(--vedari-surface)" }}
        >
          <h2 className="text-xl font-medium mb-2" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            {inTwa ? C.twaNoBillingHeading : C.webHeading}
          </h2>
          <p className="text-sm leading-relaxed mb-5" style={{ color: "var(--vedari-ink-soft)" }}>
            {inTwa ? C.twaNoBillingBody : C.webBody}
          </p>
          <a
            data-testid="paywall-android-link"
            href="https://play.google.com/store/apps/details?id=life.vedari.twa"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-5 py-3 rounded-full text-sm font-medium transition-transform active:scale-95"
            style={{ backgroundColor: "var(--vedari-gold)", color: "var(--vedari-cream)" }}
          >
            {inTwa ? C.twaNoBillingCta : C.webCta}
            <ArrowRight size={16} strokeWidth={2} />
          </a>
        </div>
      )}

      <div className="mt-8 text-center">
        <a
          data-testid="paywall-back-today"
          href="/app/today"
          className="text-sm underline-offset-4 hover:underline"
          style={{ color: "var(--vedari-ink-soft)" }}
        >
          {C.todayLink}
        </a>
      </div>

      <button
        data-testid="paywall-diag-toggle"
        onClick={() => setShowDiag((v) => !v)}
        className="mt-10 mx-auto flex items-center gap-1 text-[10px] opacity-50"
        style={{ color: "var(--vedari-ink-soft)" }}
      >
        <Info size={10} strokeWidth={1.5} />
        {showDiag ? "Hide diagnostics" : "Show diagnostics"}
      </button>
      {showDiag && (
        <pre
          data-testid="paywall-diag"
          className="mt-2 text-[10px] p-3 rounded-md overflow-auto opacity-70"
          style={{ backgroundColor: "var(--vedari-surface)", color: "var(--vedari-ink-soft)" }}
        >
{JSON.stringify({
  in_twa: inTwa,
  billing_available: billingAvailable,
  sku_details_count: Array.isArray(skuDetails) ? skuDetails.length : null,
  sku_fetch_error: skuError,
  last_error: lastError,
  ...diag,
}, null, 2)}
        </pre>
      )}
    </div>
  );
}
